import React, { useState, useEffect } from 'react';

interface AISuggestion {
  id: string;
  type: 'measurement' | 'style' | 'fabric' | 'pricing' | 'timeline';
  title: string;
  description: string;
  confidence: number;
  isApplied: boolean;
  createdAt: string;
}

interface ImageAnalysis {
  id: string;
  imageUrl: string;
  analysis: {
    bodyType: string;
    measurements: Record<string, number>;
    styleRecommendations: string[];
    fabricSuggestions: string[];
  };
  createdAt: string;
}

interface AISettings {
  autoSuggestions: boolean;
  imageRecognition: boolean;
  styleRecommendations: boolean;
  pricingOptimization: boolean;
  qualityCheck: boolean;
  language: string;
}

const AIFeatures: React.FC = () => {
  const [suggestions, setSuggestions] = useState<AISuggestion[]>([]);
  const [imageAnalyses, setImageAnalyses] = useState<ImageAnalysis[]>([]);
  const [settings, setSettings] = useState<AISettings>({
    autoSuggestions: true,
    imageRecognition: true,
    styleRecommendations: true,
    pricingOptimization: true,
    qualityCheck: true,
    language: 'en'
  });
  const [activeTab, setActiveTab] = useState<'suggestions' | 'image-analysis' | 'settings' | 'demo'>('suggestions');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<any>(null);

  useEffect(() => {
    // Load mock data
    const mockSuggestions: AISuggestion[] = [
      {
        id: '1',
        type: 'measurement',
        title: 'Adjust Sleeve Length',
        description: 'Based on customer height, recommend 1 inch longer sleeves for better fit',
        confidence: 0.92,
        isApplied: false,
        createdAt: '2024-01-15T10:30:00Z'
      },
      {
        id: '2',
        type: 'style',
        title: 'Modern Collar Style',
        description: 'Customer\'s face shape suggests a spread collar would be more flattering',
        confidence: 0.87,
        isApplied: true,
        createdAt: '2024-01-15T11:20:00Z'
      },
      {
        id: '3',
        type: 'fabric',
        title: 'Premium Cotton Blend',
        description: 'For this style and climate, recommend 60/40 cotton-polyester blend',
        confidence: 0.94,
        isApplied: false,
        createdAt: '2024-01-15T12:15:00Z'
      },
      {
        id: '4',
        type: 'pricing',
        title: 'Competitive Pricing',
        description: 'Similar orders in your area average ₹2,800. Consider ₹2,600 for this customer',
        confidence: 0.89,
        isApplied: false,
        createdAt: '2024-01-15T13:45:00Z'
      },
      {
        id: '5',
        type: 'timeline',
        title: 'Realistic Timeline',
        description: 'Based on current workload, suggest 7-10 days for delivery',
        confidence: 0.91,
        isApplied: true,
        createdAt: '2024-01-15T14:30:00Z'
      }
    ];

    const mockImageAnalyses: ImageAnalysis[] = [
      {
        id: '1',
        imageUrl: '/api/mock-image-1.jpg',
        analysis: {
          bodyType: 'Athletic',
          measurements: {
            chest: 42,
            waist: 34,
            shoulders: 18,
            armLength: 25,
            height: 70
          },
          styleRecommendations: [
            'Fitted shirts with stretch fabric',
            'Modern slim-fit trousers',
            'Structured blazers for formal wear'
          ],
          fabricSuggestions: [
            'Cotton-polyester blend for durability',
            'Stretch denim for comfort',
            'Lightweight wool for formal wear'
          ]
        },
        createdAt: '2024-01-15T10:30:00Z'
      }
    ];

    setSuggestions(mockSuggestions);
    setImageAnalyses(mockImageAnalyses);
  }, []);

  const handleSuggestionToggle = (suggestionId: string) => {
    setSuggestions(prev => 
      prev.map(suggestion => 
        suggestion.id === suggestionId 
          ? { ...suggestion, isApplied: !suggestion.isApplied }
          : suggestion
      )
    );
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageAnalysis = async () => {
    if (!uploadedImage) return;

    setIsAnalyzing(true);
    
    // Simulate AI analysis
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    const mockAnalysis = {
      bodyType: 'Average',
      measurements: {
        chest: 38,
        waist: 32,
        shoulders: 16.5,
        armLength: 24,
        height: 68,
        neck: 15,
        sleeveLength: 25,
        inseam: 32
      },
      styleRecommendations: [
        'Classic fit shirts for comfort',
        'Straight-leg trousers',
        'Traditional blazers'
      ],
      fabricSuggestions: [
        '100% cotton for breathability',
        'Cotton-linen blend for summer',
        'Wool blend for winter'
      ],
      confidence: 0.88
    };
    
    setAnalysisResult(mockAnalysis);
    setIsAnalyzing(false);
  };

  const getSuggestionIcon = (type: string) => {
    switch (type) {
      case 'measurement': return '📏';
      case 'style': return '👔';
      case 'fabric': return '🧵';
      case 'pricing': return '💰';
      case 'timeline': return '⏰';
      default: return '💡';
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return 'text-green-600 bg-green-100';
    if (confidence >= 0.8) return 'text-blue-600 bg-blue-100';
    if (confidence >= 0.7) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const renderSuggestions = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">AI Suggestions</h3>
        <div className="flex space-x-2">
          <select className="border border-gray-300 rounded-md px-3 py-1 text-sm">
            <option>All Types</option>
            <option>Measurement</option>
            <option>Style</option>
            <option>Fabric</option>
            <option>Pricing</option>
            <option>Timeline</option>
          </select>
          <select className="border border-gray-300 rounded-md px-3 py-1 text-sm">
            <option>All Status</option>
            <option>Applied</option>
            <option>Pending</option>
          </select>
        </div>
      </div>

      <div className="space-y-4">
        {suggestions.map((suggestion) => (
          <div key={suggestion.id} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start">
                <div className="text-2xl mr-3 mt-1">{getSuggestionIcon(suggestion.type)}</div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-medium text-gray-900">{suggestion.title}</h4>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getConfidenceColor(suggestion.confidence)}`}>
                        {Math.round(suggestion.confidence * 100)}% confidence
                      </span>
                      <button
                        onClick={() => handleSuggestionToggle(suggestion.id)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                          suggestion.isApplied ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                          suggestion.isApplied ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">{suggestion.description}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    {new Date(suggestion.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-4 flex space-x-2">
              <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                Apply
              </button>
              <button className="text-gray-600 hover:text-gray-700 text-sm font-medium">
                Learn More
              </button>
              <button className="text-red-600 hover:text-red-700 text-sm font-medium">
                Dismiss
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderImageAnalysis = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Image Analysis</h3>
      
      <div className="grid gap-6 md:grid-cols-2">
        <div className="bg-white rounded-lg shadow p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Upload Customer Photo</h4>
          
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
              />
              <label htmlFor="image-upload" className="cursor-pointer">
                <div className="text-4xl mb-2">📷</div>
                <p className="text-sm text-gray-600">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  PNG, JPG, JPEG up to 10MB
                </p>
              </label>
            </div>
            
            {uploadedImage && (
              <div className="text-center">
                <img
                  src={uploadedImage}
                  alt="Uploaded"
                  className="max-w-full h-64 object-cover rounded-lg mx-auto"
                />
                <button
                  onClick={handleImageAnalysis}
                  disabled={isAnalyzing}
                  className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {isAnalyzing ? 'Analyzing...' : 'Analyze Image'}
                </button>
              </div>
            )}
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Analysis Results</h4>
          
          {analysisResult ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Body Type</label>
                <p className="text-sm text-gray-900">{analysisResult.bodyType}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Estimated Measurements</label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {Object.entries(analysisResult.measurements).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-sm text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1')}:</span>
                      <span className="text-sm font-medium">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Style Recommendations</label>
                <ul className="mt-2 space-y-1">
                  {analysisResult.styleRecommendations.map((rec, index) => (
                    <li key={index} className="text-sm text-gray-600">• {rec}</li>
                  ))}
                </ul>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Fabric Suggestions</label>
                <ul className="mt-2 space-y-1">
                  {analysisResult.fabricSuggestions.map((fabric, index) => (
                    <li key={index} className="text-sm text-gray-600">• {fabric}</li>
                  ))}
                </ul>
              </div>
              
              <div className="pt-4 border-t">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {Math.round(analysisResult.confidence * 100)}% confidence
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <div className="text-4xl mb-2">🤖</div>
              <p>Upload a photo to get AI analysis</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">AI Settings</h3>
      
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h4 className="text-md font-medium text-gray-900">AI Features</h4>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Auto Suggestions</p>
              <p className="text-sm text-gray-500">Automatically suggest measurements and styles</p>
            </div>
            <button
              onClick={() => setSettings({...settings, autoSuggestions: !settings.autoSuggestions})}
              className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                settings.autoSuggestions ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                settings.autoSuggestions ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Image Recognition</p>
              <p className="text-sm text-gray-500">Analyze customer photos for measurements</p>
            </div>
            <button
              onClick={() => setSettings({...settings, imageRecognition: !settings.imageRecognition})}
              className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                settings.imageRecognition ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                settings.imageRecognition ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Style Recommendations</p>
              <p className="text-sm text-gray-500">Suggest styles based on body type</p>
            </div>
            <button
              onClick={() => setSettings({...settings, styleRecommendations: !settings.styleRecommendations})}
              className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                settings.styleRecommendations ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                settings.styleRecommendations ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Pricing Optimization</p>
              <p className="text-sm text-gray-500">Suggest optimal pricing based on market data</p>
            </div>
            <button
              onClick={() => setSettings({...settings, pricingOptimization: !settings.pricingOptimization})}
              className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                settings.pricingOptimization ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                settings.pricingOptimization ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Quality Check</p>
              <p className="text-sm text-gray-500">AI-powered quality assurance</p>
            </div>
            <button
              onClick={() => setSettings({...settings, qualityCheck: !settings.qualityCheck})}
              className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                settings.qualityCheck ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                settings.qualityCheck ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDemo = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">AI Demo</h3>
      
      <div className="grid gap-6 md:grid-cols-2">
        <div className="bg-white rounded-lg shadow p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Smart Measurement</h4>
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-6xl mb-4">📏</div>
              <p className="text-sm text-gray-600">
                Upload a photo and get instant measurement suggestions
              </p>
            </div>
            <button className="w-full bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700">
              Try Smart Measurement
            </button>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Style Advisor</h4>
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-6xl mb-4">👔</div>
              <p className="text-sm text-gray-600">
                Get personalized style recommendations
              </p>
            </div>
            <button className="w-full bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700">
              Try Style Advisor
            </button>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Pricing Assistant</h4>
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-6xl mb-4">💰</div>
              <p className="text-sm text-gray-600">
                Get optimal pricing suggestions
              </p>
            </div>
            <button className="w-full bg-purple-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-purple-700">
              Try Pricing Assistant
            </button>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Quality Checker</h4>
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-6xl mb-4">✅</div>
              <p className="text-sm text-gray-600">
                AI-powered quality assurance
              </p>
            </div>
            <button className="w-full bg-orange-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-orange-700">
              Try Quality Checker
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">AI Features</h1>
        <p className="text-gray-600 mt-2">
          Intelligent suggestions and image recognition for better tailoring
        </p>
      </div>

      <div className="mb-6">
        <nav className="flex space-x-8">
          {[
            { id: 'suggestions', label: 'Suggestions', icon: '💡' },
            { id: 'image-analysis', label: 'Image Analysis', icon: '📷' },
            { id: 'settings', label: 'Settings', icon: '⚙️' },
            { id: 'demo', label: 'Demo', icon: '🎯' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'suggestions' && renderSuggestions()}
      {activeTab === 'image-analysis' && renderImageAnalysis()}
      {activeTab === 'settings' && renderSettings()}
      {activeTab === 'demo' && renderDemo()}
    </div>
  );
};

export default AIFeatures; 